import { useState, useEffect, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { X, Mic, MicOff, Send, Minimize2, Maximize2 } from 'lucide-react';
import VoicePlayer from '@/components/VoicePlayer';
import { PacReplyView } from '@/components/PacReplyChips';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AICoachChatbot({ user, isOpen, onClose, onMinimize, isMinimized }) {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [aiModel, setAiModel] = useState('claude');
  const [hasGreeted, setHasGreeted] = useState(false);
  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);

  // Load avatar and send greeting on mount
  useEffect(() => {
    loadAvatar();
    if (isOpen && !hasGreeted) {
      sendGreeting();
      setHasGreeted(true);
    }
  }, [isOpen]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Initialize speech recognition with robust error handling
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      console.log('Speech Recognition not supported in this browser');
      return;
    }

    try {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'en-US';
      recognitionRef.current.maxAlternatives = 1;

      recognitionRef.current.onstart = () => {
        console.log('Voice recognition started');
        setIsListening(true);
      };

      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        console.log('Voice transcript:', transcript);
        setInputText(transcript);
        setIsListening(false);
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        
        // Handle specific error types gracefully
        switch (event.error) {
          case 'not-allowed':
          case 'permission-denied':
            toast.error('Microphone access denied. Please allow microphone access in your browser settings.');
            break;
          case 'no-speech':
            // Silent - no speech detected, this is normal
            break;
          case 'audio-capture':
            toast.error('No microphone found. Please connect a microphone.');
            break;
          case 'network':
            toast.error('Network error. Please check your internet connection.');
            break;
          case 'aborted':
            // User or system aborted - no error message needed
            break;
          default:
            // Don't show error for common non-critical errors
            if (event.error !== 'service-not-allowed') {
              console.warn(`Voice input warning: ${event.error}`);
            }
        }
      };

      recognitionRef.current.onend = () => {
        console.log('Voice recognition ended');
        setIsListening(false);
      };
    } catch (error) {
      console.error('Failed to initialize speech recognition:', error);
    }

    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          // Ignore cleanup errors
        }
      }
    };
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadAvatar = async () => {
    try {
      const response = await axios.get(`${API}/coach-avatar/${user.id}`);
      if (response.data.avatar_url) {
        setAvatarUrl(response.data.avatar_url);
      }
    } catch (error) {
      // No avatar set, use default
    }
  };

  const sendGreeting = async () => {
    const firstName = user.name?.split(' ')[0] || 'Coach';
    const greetingMessage = {
      role: 'assistant',
      content: `Hey ${firstName}! 👋 Great to see you! I'm your AI coaching assistant. I can help you with:\n\n• Player stats and roster info\n• Schedule and upcoming games\n• Practice planning tips\n• Team performance insights\n\nWhat can I help you with today?`,
      timestamp: new Date().toISOString()
    };
    setMessages([greetingMessage]);
  };

  const toggleVoiceInput = () => {
    if (!recognitionRef.current) {
      toast.error('Voice recognition not supported in this browser. Please use Chrome or Edge.');
      return;
    }

    if (isListening) {
      try {
        recognitionRef.current.stop();
      } catch (error) {
        console.error('Error stopping recognition:', error);
        setIsListening(false);
      }
    } else {
      try {
        // Ensure any previous session is stopped first
        try {
          recognitionRef.current.stop();
        } catch (e) {
          // Ignore - may not be running
        }
        
        // Small delay before starting new session to prevent conflicts
        setTimeout(() => {
          try {
            recognitionRef.current?.start();
            toast.info('Listening... Speak now');
          } catch (error) {
            if (error.message?.includes('already started')) {
              // Already listening, ignore
              setIsListening(true);
            } else {
              console.error('Speech recognition start error:', error);
              toast.error('Could not start voice input. Please try again.');
              setIsListening(false);
            }
          }
        }, 100);
      } catch (error) {
        console.error('Speech recognition error:', error);
        toast.error('Could not start voice input. Please try again.');
        setIsListening(false);
      }
    }
  };

  const [resolvedPendingActions, setResolvedPendingActions] = useState({});

  const handlePendingAction = async (pendingActionId, action) => {
    if (resolvedPendingActions[pendingActionId]) return;
    setResolvedPendingActions((prev) => ({ ...prev, [pendingActionId]: true }));
    try {
      await axios.post(`${API}/player-intel/pending-actions/${pendingActionId}/${action}`, { user_id: user.id });
      toast.success(action === 'confirm' ? 'Saved to development profile' : 'Cancelled');
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: action === 'confirm'
          ? "Done — I've saved that to the player's development profile."
          : "No problem, I didn't save that.",
        timestamp: new Date().toISOString(),
      }]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process that action');
    }
  };

  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!inputText.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: inputText.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/ai-coach/chat`, {
        user_id: user.id,
        team_id: user.team_id,
        message: userMessage.content,
        coach_name: user.name,
        model: aiModel,
        conversation_history: messages.slice(-6).map((m) => ({ role: m.role, content: m.content })),
      });

      const assistantMessage = {
        role: 'assistant',
        content: response.data.response,
        structured: response.data.structured || null,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = {
        role: 'assistant',
        content: "I'm having trouble connecting right now. Please try again in a moment.",
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  if (isMinimized) {
    return (
      <div 
        className="fixed bottom-6 right-6 z-50 cursor-pointer"
        onClick={onMinimize}
        data-testid="ai-coach-minimized"
      >
        <div className="relative">
          <div className="w-16 h-16 rounded-full bg-primary shadow-lg flex items-center justify-center overflow-hidden border-4 border-white hover:scale-110 transition-transform">
            {avatarUrl ? (
              <img src={avatarUrl} alt="AI Coach" className="w-full h-full object-cover" />
            ) : (
              <div className="text-2xl">🏀</div>
            )}
          </div>
          <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white animate-pulse"></div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="fixed bottom-6 right-6 z-50 w-96 max-h-[600px] shadow-2xl rounded-xl overflow-hidden"
      data-testid="ai-coach-chatbot"
    >
      <Card className="border-2 border-primary h-full flex flex-col">
        {/* Header */}
        <CardHeader className="bg-primary text-primary-foreground p-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center overflow-hidden">
                {avatarUrl ? (
                  <img src={avatarUrl} alt="AI Coach" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-2xl">🏀</span>
                )}
              </div>
              <div>
                <CardTitle className="text-lg font-bold">AI Coach Assistant</CardTitle>
                <div className="flex items-center gap-1 mt-1" data-testid="ai-model-picker">
                  <button
                    onClick={() => setAiModel('claude')}
                    className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full transition-colors ${aiModel === 'claude' ? 'bg-white text-primary' : 'bg-white/20 text-primary-foreground/80 hover:bg-white/30'}`}
                    data-testid="ai-model-claude-btn"
                  >
                    Claude
                  </button>
                  <button
                    onClick={() => setAiModel('gpt')}
                    className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full transition-colors ${aiModel === 'gpt' ? 'bg-white text-primary' : 'bg-white/20 text-primary-foreground/80 hover:bg-white/30'}`}
                    data-testid="ai-model-gpt-btn"
                  >
                    GPT
                  </button>
                </div>
              </div>
            </div>
            <div className="flex gap-1">
              <Button 
                variant="ghost" 
                size="icon" 
                className="text-primary-foreground hover:bg-white/20 h-8 w-8"
                onClick={onMinimize}
                data-testid="minimize-chatbot"
              >
                <Minimize2 className="h-4 w-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="icon" 
                className="text-primary-foreground hover:bg-white/20 h-8 w-8"
                onClick={onClose}
                data-testid="close-chatbot"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>

        {/* Messages */}
        <CardContent className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 min-h-[300px] max-h-[400px]">
          {messages.map((message, index) => (
            <div 
              key={index} 
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div 
                className={`max-w-[85%] p-3 rounded-xl ${
                  message.role === 'user' 
                    ? 'bg-primary text-primary-foreground rounded-br-sm' 
                    : 'bg-white border shadow-sm rounded-bl-sm'
                }`}
              >
                {message.role === 'assistant' && message.structured ? (
                  <PacReplyView structured={message.structured} />
                ) : (
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                )}
                {message.role === 'assistant' && message.structured?._meta?.pending_actions?.length > 0 && (
                  <div className="mt-2 space-y-2" data-testid="pending-actions-block">
                    {message.structured._meta.pending_actions.map((pa) => {
                      const resolved = !!resolvedPendingActions[pa.pending_action_id];
                      return (
                        <div key={pa.pending_action_id} className="p-2 bg-amber-50 border border-amber-300 rounded-md text-xs space-y-1" data-testid={`pending-action-${pa.pending_action_id}`}>
                          <p className="font-medium text-amber-900">{pa.summary}</p>
                          <div className="flex gap-2">
                            <Button size="sm" className="h-7 text-xs" disabled={resolved} onClick={() => handlePendingAction(pa.pending_action_id, 'confirm')} data-testid={`confirm-pending-${pa.pending_action_id}`}>
                              Confirm
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={resolved} onClick={() => handlePendingAction(pa.pending_action_id, 'cancel')} data-testid={`cancel-pending-${pa.pending_action_id}`}>
                              Cancel
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                <div className="flex items-center justify-between mt-1 gap-2">
                  <p className={`text-xs ${message.role === 'user' ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                    {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                  {message.role === 'assistant' && (
                    <VoicePlayer text={message.content} userId={user.id} source="chat" />
                  )}
                </div>
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-white border shadow-sm rounded-xl rounded-bl-sm p-3">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </CardContent>

        {/* Input */}
        <div className="p-4 border-t bg-white flex-shrink-0">
          <form onSubmit={sendMessage} className="flex gap-2">
            <Button
              type="button"
              variant={isListening ? 'destructive' : 'outline'}
              size="icon"
              onClick={toggleVoiceInput}
              className="flex-shrink-0"
              data-testid="voice-input-btn"
            >
              {isListening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
            </Button>
            <Input
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder="Ask me anything..."
              className="flex-1"
              disabled={isLoading}
              data-testid="chat-input"
            />
            <Button 
              type="submit" 
              size="icon" 
              disabled={!inputText.trim() || isLoading}
              data-testid="send-message-btn"
            >
              <Send className="h-4 w-4" />
            </Button>
          </form>
        </div>
      </Card>
    </div>
  );
}
